/**
 * ArrayEditor - Editor for array/list values
 *
 * Renders arrays as a list of editable items with add/remove functionality.
 * Supports string arrays and can be extended for typed arrays.
 */

import { useCallback, useState, useEffect, useRef } from "react";
import {
  Box,
  Input,
  Sheet,
  Button,
  IconButton,
  Typography,
  Stack,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { GridRenderEditCellParams, useGridApiContext } from "@mui/x-data-grid";

interface ArrayEditorProps extends GridRenderEditCellParams {
  nullable?: boolean;
}

export default function ArrayEditor({
  id,
  value,
  field,
  nullable,
}: ArrayEditorProps) {
  const apiRef = useGridApiContext();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(true);

  // Parse the array value
  const [items, setItems] = useState<string[]>(() => {
    if (value === null || value === undefined) return [];
    if (Array.isArray(value)) return value.map(String);
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.map(String);
        return [value];
      } catch {
        // Might be a comma-separated string
        return value
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
    }
    return [String(value)];
  });

  // Update the cell value when items change
  useEffect(() => {
    apiRef.current.setEditCellValue({
      id,
      field,
      value: items.length > 0 ? items : null,
    });
  }, [items, apiRef, id, field]);

  const handleItemChange = useCallback((index: number, newValue: string) => {
    setItems((prev) => {
      const updated = [...prev];
      updated[index] = newValue;
      return updated;
    });
  }, []);

  const handleAddItem = useCallback(() => {
    setItems((prev) => [...prev, ""]);
  }, []);

  const handleRemoveItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSave = useCallback(() => {
    // Filter out empty items before saving
    const cleanedItems = items.filter((item) => item.trim() !== "");
    apiRef.current.setEditCellValue({
      id,
      field,
      value: cleanedItems.length > 0 ? cleanedItems : null,
    });
    apiRef.current.stopCellEditMode({ id, field });
    setOpen(false);
  }, [apiRef, id, field, items]);

  const handleCancel = useCallback(() => {
    apiRef.current.stopCellEditMode({ id, field, ignoreModifications: true });
    setOpen(false);
  }, [apiRef, id, field]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        handleCancel();
      }
    },
    [handleCancel],
  );

  if (!open) return null;

  return (
    <Box
      ref={anchorRef}
      sx={{ position: "relative", width: "100%", height: "100%" }}
    >
      <Sheet
        variant="outlined"
        sx={{
          position: "absolute",
          top: "100%",
          left: 0,
          zIndex: 1300,
          p: 2,
          width: 350,
          maxWidth: "90vw",
          maxHeight: "50vh",
          overflow: "auto",
          borderRadius: "md",
          boxShadow: "lg",
        }}
        onKeyDown={handleKeyDown}
      >
        <Typography level="body-sm" sx={{ mb: 1, color: "text.secondary" }}>
          Edit List ({items.length} items)
        </Typography>

        <Stack spacing={1}>
          {items.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <DragIndicatorIcon
                fontSize="small"
                sx={{ color: "text.disabled", cursor: "grab" }}
              />
              <Input
                size="sm"
                value={item}
                onChange={(e) => handleItemChange(index, e.target.value)}
                placeholder={`Item ${index + 1}`}
                autoFocus={index === items.length - 1 && item === ""}
                sx={{ flex: 1 }}
              />
              <IconButton
                size="sm"
                color="danger"
                onClick={() => handleRemoveItem(index)}
                disabled={items.length === 1 && !nullable}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}

          {items.length === 0 && (
            <Box sx={{ py: 2, textAlign: "center" }}>
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                No items. Click "Add Item" to create one.
              </Typography>
            </Box>
          )}
        </Stack>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
          <Button
            size="sm"
            variant="plain"
            startDecorator={<AddIcon />}
            onClick={handleAddItem}
          >
            Add Item
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="sm" variant="plain" onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="sm" variant="solid" onClick={handleSave}>
              Save
            </Button>
          </Box>
        </Box>

        <Typography level="body-xs" sx={{ mt: 1, color: "text.secondary" }}>
          Empty items will be removed on save
        </Typography>
      </Sheet>
    </Box>
  );
}
